import React, { useEffect, useMemo, useRef, useState } from 'react';
import Form from '../../molecules/Form/Form';
import Field from '../Field';
import Button from '../../molecules/Button';
import useForm from '../../molecules/Form/useForm';
import iconEmailVerification from '../../../assets/images/email-verification-icon.svg';
import iconFile from '../../../assets/images/connect-email-icon.svg';
import outlookIcon from '../../../assets/images/outlook-icon.svg';
import { FcGoogle } from "react-icons/fc";
import { EmailModalStyled, StepCircle, StepConnector, StepItem, StepLabel, StepsContainer } from './ConnectEmailDomainModal.styles';
import { useContextHook } from 'use-context-hook';
import Toast from '../../molecules/Toast';
import { AuthContext } from '../../../Context/authContext';
import campaignService from '../../../services/campaignService';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { sampleCsvColumns, sampleCsvRows, warmupEnabledDuration, warmupEnabledOptions } from '../../Constant';
import { convertToFormData, downloadCSV, handleCampaignDrop, handleCopy, normalizeKeys } from '../../../helpers/common';
import { IoArrowForwardOutline, IoClose } from 'react-icons/io5';
import { PiMicrosoftOutlookLogoLight } from 'react-icons/pi';
import { IoIosArrowBack, IoIosCheckmarkCircle, IoMdVideocam } from 'react-icons/io';
import ShootingStars from '../ShootingStars';
import { FaFileCsv } from 'react-icons/fa';

const ConnectEmailModal = ({ setOpenConnectEmailModal }) => {
  const [form] = useForm();
  const [bulkForm] = useForm();
  const navigate = useNavigate();

  const [singleAccountStep, setSingleAccountStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState('noSelection');
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
  const [outlookAuthLoading, setOutlookAuthLoading] = useState(false);
  const [selectedGoogleProviderStep, setSelectedGoogleProviderStep] = useState(1);
  const [selectedOutlookProviderStep, setSelectedOutlookProviderStep] = useState(1);
  const [bulkData, setBulkData] = useState(null);
  const [protocol, setProtocol] = useState('tls');
  const [firstStepPayload, setFirstStepPayload] = useState(null);
  const [secondStepPayload, setSecondStepPayload] = useState(null);

  const { refetch, user } = useContextHook(AuthContext, v => ({
    refetch: v.refetch,
    user: v.user,
  }));

  const handleFinish = () => {
    // trigger shooting stars on email accounts page
    sessionStorage.setItem('showStars', 'true');
    navigate('/email-accounts');
  };

  const handleDownloadSampleCsv = () => {
    downloadCSV('smtp_accounts_sample.csv', sampleCsvRows, sampleCsvColumns);
  };

  async function handleSubmit(e) {
    setLoading(true);
    try {
      const payload = {
        email: e?.email,
        warmup_status: 'completed',
        warmup_reputation: 0,
        first_name: e?.first_name,
        last_name: e?.last_name,
        provider: "gmail",
        imap_host: "imap.gmail.com",
        imap_port: 993,
        app_password: e?.app_password,
        smtp_host: "smtp.gmail.com",
        smtp_port: 587,
        use_tls: true,
        use_ssl: false,
      };

      const res = await campaignService.connectEmail(payload);
      if (res?.success) {
        setSelectedGoogleProviderStep(5);
        sessionStorage.setItem('showStars', 'true');
        navigate('/email-accounts');
        Toast({ type: 'success', message: res?.message });
      }

    } catch (err) {
      Toast({ type: 'error', message: err?.message });
    } finally {
      setLoading(false);
    }
  }

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleEmailAutoFill = async (email) => {
    if (!email || typeof email !== 'string') return;

    const parts = email.split('@');
    if (parts.length !== 2) return;

    const domain = parts[1].trim().toLowerCase();
    if (!domain) return;

    // Defaults for custom providers (e.g. atif@houdiny.ai -> mail.website.com)
    const imapPort = 993;
    const smtpPort = 587;

    await delay(200);

    if (singleAccountStep === 5) {
      form.setFieldsValue({
        imap_port: imapPort,
      });
    }

    if (singleAccountStep === 6) {
      form.setFieldsValue({
        smtp_port: smtpPort,
      });
    }

  };

  useEffect(() => {
    if (singleAccountStep === 5 && firstStepPayload?.email) {
      handleEmailAutoFill(firstStepPayload.email);
    }

    if (singleAccountStep === 6 && secondStepPayload?.imap_host) {
      handleEmailAutoFill(firstStepPayload.email);
    }
  }, [singleAccountStep, firstStepPayload]);

  const getProviderFromSmtpHost = (host) => {
    if (!host || typeof host !== 'string') return null;

    const trimmed = host.trim().toLowerCase();
    const parts = trimmed.split('.').filter(Boolean);

    if (parts.length < 2) return null;

    // Common pattern: smtp.spacemail.com -> spacemail
    if (parts[0] === 'mail' && parts.length >= 3) {
      return parts[1];
    }

    // Fallback: take the first segment
    return parts[0];
  };

  async function handleSingleAccountSubmit(e) {
    if (singleAccountStep === 4) {
      const payload = {
        email: e?.email,
        first_name: e?.first_name,
        last_name: e?.last_name,
        app_password: e?.app_password,
      };
      setFirstStepPayload(payload);
      setSingleAccountStep(5);
    } else if (singleAccountStep === 5) {
      const payload = {
        imap_host: e?.imap_host,
        imap_port: Number(e?.imap_port) || 993,
      };
      setSecondStepPayload(payload);
      setSingleAccountStep(6);
    } else if (singleAccountStep === 6) {
      setLoading(true);
      try {
        const inferredProvider = getProviderFromSmtpHost(e?.smtp_host) || 'custom';

        const payload = {
          ...firstStepPayload,
          ...secondStepPayload,
          warmup_status: 'completed',
          warmup_reputation: 0,
          provider: inferredProvider,
          smtp_host: e?.smtp_host,
          smtp_port: Number(e?.smtp_port) || 587,
          use_tls: protocol === 'tls',
          use_ssl: protocol === 'ssl',
        };

        const res = await campaignService.connectEmail(payload);
        if (res?.success) {
          sessionStorage.setItem('showStars', 'true');
          navigate('/email-accounts');
          Toast({ type: 'success', message: res?.message });
        }
      } catch (err) {
        Toast({ type: 'error', message: err?.message });
      } finally {
        setLoading(false);
      }
    }
  }

  const handleCsvSubmit = async (e) => {
    setLoading(true);
    try {
      const csvFile = formData?.rows || e?.csvfile;

      const normalizedCsvFile = Array.isArray(csvFile)
        ? csvFile.map(row => (typeof row === 'object' && row !== null ? normalizeKeys(row) : row))
        : csvFile;

      const payload = {
        smtpAccounts: normalizedCsvFile,
      };

      const res = await campaignService.bulkConnectEmails(payload);

      if (res?.success) {
        setSingleAccountStep(2);
        setBulkData(res?.data || null);
        Toast({ type: 'success', message: res?.message });
      }
    } catch (err) {
      Toast({ type: 'error', message: err?.message });
    } finally {
      setLoading(false);
    }
  }

  async function handleConnectWithAuth() {
    setGoogleAuthLoading(true);
    try {
      const res = await campaignService.connectWithAuth();
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch (err) {
      console.error('Google connect failed', err);
    } finally {
      setGoogleAuthLoading(false);
    }
  }

  async function handleConnectWithOutLookAuth() {
    setOutlookAuthLoading(true);
    try {
      const res = await campaignService.connectWithOutlookAuth();
      if (res?.redirectUrl) {
        window.location.href = res.redirectUrl;
      }
    } catch (err) {
      console.error('Outlook connect failed', err);
    } finally {
      setOutlookAuthLoading(false);
    }

  }

  const Status = data => {
    switch (data.status) {
      case 'Success':
        return <div className="badge center success">{data?.status}</div>;
      case 'Failed':
        return <div className="badge center failed">{data?.status}</div>;
      case 'Exist':
        return <div className="badge center running">{data?.status}</div>;
      default:
        return <div className="badge neutral right">Unknown</div>;
    }
  };



  return (
    <EmailModalStyled>

      <div className="head">
        <button className="icon-holder" onClick={() => navigate('/email-accounts')}>
          <IoIosArrowBack />
        </button>
        <span className="title">Connecting Email Account</span>
      </div>
      <div className='connect-email-content-holder'>

        {/* <Form form={form} className="form-area" onSubmit={handleSubmit}> */}

        {/* Step 1 */}
        <div className='content-holder'>
          {activeTab === 'noSelection' && (
            <div className="options-wrapper">
              <span className="title">Select the email type</span>
              <span className='sub-heading'>Choose your email provider to start sending campaigns safely.</span>
              <button
                type="button"
                className="tabBtn" onClick={() => setActiveTab('anyProvider')}>
                {/* <div className="btn-content"> */}
                <div className="icon-box">
                  <img src={iconFile} width="25px" alt="file" />
                </div>
                <div className="text-box">
                  <span className="subtitle">Any Provider</span>
                  <span className="title">IMAP / SMTP</span>
                </div>
                {/* </div> */}
              </button>
              <button
                type="button"
                className="tabBtn" onClick={() => setActiveTab('outlook')}>
                <div className="icon-box">
                  <img src={outlookIcon} width="32px" alt="folder" />
                </div>
                <div className="text-box">
                  <span className="subtitle">Microsoft</span>
                  <span className="title">Office 365 / Outlook</span>
                </div>
              </button>
              <button
                type="button"
                className="tabBtn" onClick={() => setActiveTab('google')}>
                <div className="icon-box">
                  <FcGoogle size={40} />
                </div>
                <div className="text-box">
                  <span className="subtitle">Google</span>
                  <span className="title">Gmail / G-Suite</span>
                </div>
              </button>
            </div>
          )}
          {activeTab === 'anyProvider' && (
            <div className="custom-provider-holder">
              <div className="select-another-provider-holder" onClick={() => { setActiveTab('noSelection'); setSingleAccountStep(0); setFirstStepPayload(null); setSecondStepPayload(null); }}>      <IoIosArrowBack /><span>Select Another Provider</span> </div>
              {singleAccountStep === 0 && (
                <div className="options-wrapper">
                  <button
                    type="button"
                    className="tabBtn"
                    onClick={() => setSingleAccountStep(4)}
                  >
                    <div className="icon-box">
                      <img src={iconFile} width="25px" alt="file" />
                    </div>
                    <div className="text-box">
                      <span className="subtitle">Any Provider</span>
                      <span className="title">Single Account</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="tabBtn" onClick={() => setSingleAccountStep(1)}>
                    <div className="icon-box">
                      {/* <img src={outlookIcon} width="32px" alt="folder" /> */}
                      <FaFileCsv size={40} color="var(--blue)" />

                    </div>
                    <div className="text-box">
                      <span className="subtitle">Upload CSV</span>
                      <span className="title">Bulk Import from CSV</span>
                    </div>
                  </button>
                </div>
              )}

              {singleAccountStep === 1 && (
                <div className="csv-file-holder">
                  <div className="download-sample-btn-holder">
                    <Button width="200px" rounded type="button" onClick={handleDownloadSampleCsv}>
                      Download Sample CSV
                    </Button>
                  </div>
                  <Form form={bulkForm} onSubmit={handleCsvSubmit}>
                    <Form.Item
                      type="csv"
                      label="Upload your CSV file"
                      rounded
                      name="csvfile"
                      noMargin
                      placeholder="csv file"

                      rules={[
                        {
                          required: true,
                          message: 'File is required to continue',
                        },
                      ]}
                      bg
                      disc="Drag & Drop or choose file to upload . Limit 200MB per file • CSV"
                      uploadTitle="Drag & Drop or choose file to upload"
                      fileSize={20}
                      icon
                      accept=".xlsx , .csv"
                      onUploadAccepted={(rawData, file) => {
                        handleCampaignDrop(rawData, file, user, setFormData, bulkForm, Toast);
                      }}
                      onRemove={() => {
                        bulkForm.setFieldsValue({ csvfile: '' });
                      }}>
                      <Field />
                    </Form.Item>
                    <div className="btn-holder">
                      <Button width="200px" rounded type="submit" loader={loading} disabled={loading}>Submit</Button>
                    </div>
                  </Form>
                </div>
              )}
              {singleAccountStep === 2 && (
                <div className="options-wrapper bulk-email-table">
                  <h2>Accounts</h2>
                  <p>Connect Multiple Email Accounts</p>
                  <div className="table-head">
                    <span>Name</span>
                    <span>Status</span>
                  </div>
                  <div className="table-body">
                    {bulkData?.result?.map((item, index) => (
                      <div className="table-row" key={index}>
                        <span>{item?.email}</span>
                        <span>{Status(item)}</span>
                      </div>
                    ))}
                  </div>
                  <Button rounded onClick={handleFinish}>Finish</Button>
                </div>
              )}

              {singleAccountStep >= 4 && (
                <div className="single-email-form">
                  <StepsContainer>
                    <StepItem>
                      <StepCircle $isActive={singleAccountStep >= 4}>
                        <StepConnector $isActive={singleAccountStep >= 5} $width="245px" />
                      </StepCircle>
                      <StepLabel $isActive={singleAccountStep >= 4} $isCurrent={singleAccountStep === 4}>
                        Basic Details                      </StepLabel>
                    </StepItem>
                    <StepItem>
                      <StepCircle $isActive={singleAccountStep >= 5}>
                        <StepConnector $isActive={singleAccountStep >= 6} $width="235px" />
                      </StepCircle>
                      <StepLabel $isActive={singleAccountStep >= 5} $isCurrent={singleAccountStep === 5}>
                        IMAP                      </StepLabel>
                    </StepItem>
                    <StepItem>
                      <StepCircle $isActive={singleAccountStep >= 6}>
                        <StepConnector $isActive={singleAccountStep >= 7} $width="235px" />
                      </StepCircle>
                      <StepLabel $isActive={singleAccountStep >= 6} $isCurrent={singleAccountStep === 6}>
                        SMTP
                      </StepLabel>
                    </StepItem>
                    <StepItem>
                      <StepCircle $isActive={singleAccountStep >= 7}>

                      </StepCircle>
                      <StepLabel $isActive={singleAccountStep >= 7} $isCurrent={singleAccountStep === 7}>
                        Finish
                      </StepLabel>
                    </StepItem>
                  </StepsContainer>
                </div>

              )}
              <Form form={form} onSubmit={handleSingleAccountSubmit}>
                {singleAccountStep === 4 && (
                  <div className="options-wrapper">
                    <h2>Enter Account Details</h2>
                    <p className="sub-heading">Provide basic information to connect your inbox.</p>
                    <div className="input-flex">
                      <Form.Item
                        label="Email Address"
                        type="text"
                        colorBg
                        inputSm
                        name="email"
                        placeholder="Enter your email address"
                        rules={[
                          {
                            required: true,
                            message: 'Email is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                      <Form.Item
                        label="Password"
                        type="password"
                        colorBg
                        inputSm
                        name="app_password"
                        placeholder="Enter your app password"
                        rules={[
                          {
                            required: true,
                            message: 'password is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                    </div>
                    <div className="input-flex margin">
                      <Form.Item
                        label="First Name"
                        type="text"
                        colorBg
                        inputSm
                        name="first_name"
                        placeholder="Enter your first name"
                        rules={[
                          {
                            required: true,
                            message: 'First name is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                      <Form.Item
                        label="Last Name"
                        type="text"
                        colorBg
                        inputSm
                        name="last_name"
                        placeholder="Enter your last name"
                        rules={[
                          {
                            required: true,
                            message: 'Last name is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                    </div>
                    <Button rounded type="submit" >Next</Button>

                  </div>
                )}
                {singleAccountStep === 5 && (
                  <div className="options-wrapper">
                    <h2>Configure IMAP</h2>
                    <p className="sub-heading">Provide basic information to connect your inbox.</p>
                    <div className="input-flex">
                    </div>
                    <div className="input-flex margin">
                      <div className="flex-item">
                        <Form.Item
                          label="IMAP Host"
                          type="text"
                          colorBg
                          inputSm
                          name="imap_host"
                          placeholder="Enter your IMAP Host"
                          rules={[
                            {
                              required: true,
                              message: 'IMAP Host is required',
                            },
                          ]}>
                          <Field />
                        </Form.Item>
                        <span className='note'>e.g. mail.spacemail.com</span>
                      </div>
                      <Form.Item
                        label="IMAP Port"
                        type="number"
                        colorBg
                        inputSm
                        name="imap_port"
                        placeholder="Enter your IMAP Port"
                        rules={[
                          {
                            required: true,
                            message: 'IMAP Port is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                    </div>
                    <Button rounded type="submit">Next</Button>
                  </div>
                )}
                {singleAccountStep === 6 && (
                  <div className="options-wrapper">
                    <h2>Configure SMTP</h2>
                    <p className="sub-heading">Enter your outgoing mail server settings to enable sending.</p>
                    <div className="input-flex margin">
                      <div className="flex-item">
                        <Form.Item
                          label="SMTP Host"
                          type="text"
                          colorBg
                          inputSm
                          name="smtp_host"
                          placeholder="Enter your SMTP Host"
                          rules={[
                            {
                              required: true,
                              message: 'SMTP Host is required',
                            },
                          ]}>
                          <Field />
                        </Form.Item>
                        <span className='note'>e.g. mail.spacemail.com</span>
                      </div>
                      <Form.Item
                        label="SMTP Port"
                        type="number"
                        colorBg
                        inputSm
                        name="smtp_port"
                        placeholder="Enter your SMTP Port"
                        rules={[
                          {
                            required: true,
                            message: 'SMTP Port is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                    </div>
                    <div className="protocols-holder">
                      <h3>Protocols</h3>
                      <div className="input-flex ">
                        <div className="checkbox-holder">
                          <Form.Item
                            noMargin
                            type="radio"
                            rounded
                            name="tls"
                            label="Use TLS"
                            value={protocol === 'tls'}
                            onChange={value => setProtocol(value.target.name)}>
                            <Field />
                          </Form.Item>
                        </div>
                        <div className="checkbox-holder">
                          <Form.Item
                            noMargin
                            type="radio"
                            rounded
                            name="ssl"
                            label="Use SSL"
                            value={protocol === 'ssl'}
                            onChange={value => setProtocol(value.target.name)}>
                            <Field />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                    <Button rounded type="submit" loader={loading} disabled={loading}>Submit</Button>
                  </div>
                )}
              </Form>
            </div>
          )}
          {activeTab === 'google' && (
            <div className="google-provider-holder">
              <div className="select-another-provider-holder" onClick={() => { setActiveTab('noSelection'); setSelectedGoogleProviderStep(1) }}><IoIosArrowBack /><span>Back</span> </div>
              <div className="options-wrapper">
                <div className="google-provider-head">
                  <FcGoogle size={60} />
                  <div className='header-content'>
                    <h3>Connect Your Google Account</h3>
                    <span>Gmail / G-Suite</span>
                  </div>
                </div>
                {selectedGoogleProviderStep === 1 && (
                  <div className="col-holder">
                    <div className="col" onClick={() => setSelectedGoogleProviderStep(2)}>
                      <div className="col-head">
                        <div>
                          <span className="heading">
                            Option 1 | oAuth
                          </span>
                          <span className="text">Best for easy non technicals</span>
                        </div>
                        <div className="recommended-tag">Recommended</div>
                      </div>
                      <ul>
                        <li><IoIosCheckmarkCircle size={20} className='icon' /> Easier to setup </li>
                        <li><IoIosCheckmarkCircle size={20} className='icon' /> More Stable & Less Disconnects</li>
                        <li><IoIosCheckmarkCircle size={20} className='icon' /> Available for G-Suite Accounts</li>
                      </ul>
                    </div>
                    <div className="col" onClick={() => setSelectedGoogleProviderStep(3)}>
                      <div className="col-head">
                        <div>
                          <span className="heading">
                            Option 2 | App Password
                          </span>
                          <span className="text">Best for technical</span>
                        </div>
                      </div>
                      <ul>
                        <li><IoIosCheckmarkCircle size={20} className='icon' />Available for Personal Accounts</li>
                        <li><IoIosCheckmarkCircle size={20} className='icon' />Requires 2 Factor Authentication</li>
                        <li><IoIosCheckmarkCircle size={20} className='icon' />More Prone to Disconnects</li>
                      </ul>
                    </div>
                  </div>
                )}
                {selectedGoogleProviderStep === 2 && (
                  <div className="oauth-holder">
                    <div className="head">
                      <div>
                        <h4>Allow Houdiny AI to access your Google workspace</h4>
                        <span>You only needs to do this once per domain</span>
                      </div>
                    </div>
                    <ol>
                      <li>Go to your <a href="https://admin.google.com/u/1/ac/owl/list?tab=configuredApps" className='bold' target="_blank" rel="noreferrer">Google Workspace Admin Panel</a></li>
                      <li>Click <span className='bold'>"Configure new app"</span></li>
                      <li>Use the following Client-ID to search for Houdiny AI :
                        <div className='client-id-holder'><span>635982347017-v5sh4tll9s10soqcitd33fl9is9icqdp.apps.googleusercontent.com</span>
                          <button onClick={() => handleCopy('635982347017-v5sh4tll9s10soqcitd33fl9is9icqdp.apps.googleusercontent.com')}>Copy</button>
                        </div>
                      </li>
                      <li>Select and approve Houdiny AI to access your Google Workspace</li>
                    </ol>
                    <Button rounded width="150px" onClick={handleConnectWithAuth} loader={googleAuthLoading} disabled={googleAuthLoading}>Submit</Button>
                  </div>
                )}
                {selectedGoogleProviderStep === 3 && (
                  <div className="oauth-holder app-password-holder">
                    <div className="head">
                      <div>
                        <h4>Enable 2-step verification & Generate App password</h4>
                        <span>You only needs to do this once per domain</span>
                      </div>
                    </div>
                    <ol>
                      <li>Go to your Google Account's <a href="https://myaccount.google.com/security" className='bold' target="_blank" rel="noreferrer">Security Settings</a></li>
                      <li>Enable <a href='https://accounts.google.com/v3/signin/challenge/pwd?TL=AHU8sQsW9jr7Njx5XiVeqA7HvKheqSlPrttVKRmTAQRqTfVdYKlZf0IhB936fJDg&authuser=0&cid=3&continue=https%3A%2F%2Fmyaccount.google.com%2Fsigninoptions%2Ftwo-step-verification&dsh=S-1071647858%3A1773137077155131&flowName=GlifWebSignIn&followup=https%3A%2F%2Fmyaccount.google.com%2Fsigninoptions%2Ftwo-step-verification&ifkv=ASfE1-qQ3X8opzzXBRXMZ0ho4TnUr8GuRdjh7Fmj8dx7Ri3I4gpmh_HxCpeyo_T4rJ-xEJiM0ZgK&rart=ANgoxceg6gHlwuaA1Wzk9oFPeeMI5eq9XQm7RobDuvI9g30WYfJJEakO4WjNtHijoM1fZSX9uIN3gLG3GFUKLk4_tYaJMQHCLkk9isGVNeTe0NXu0zxw7sQ&rpbg=1&service=accountsettings' className='bold' target="_blank" rel="noreferrer">2-Step Verification</a></li>
                      <li>Create an <a href='https://accounts.google.com/v3/signin/challenge/pwd?TL=AHU8sQvgG_Ymr01qvYVGeUKicKOYvUnFwt90xepPZhuxfXJM_Y-D60Na4NQlpZdg&authuser=0&cid=3&continue=https%3A%2F%2Fmyaccount.google.com%2Fapppasswords&dsh=S-314868240%3A1773137146604394&flowName=GlifWebSignIn&followup=https%3A%2F%2Fmyaccount.google.com%2Fapppasswords&ifkv=ASfE1-rjTCo9WNKoBiGI0OH2BJYI40iBgm9_cbIn4iEcWfx9MJQrP_4wDDdRpxbnz8_pt60wF3cZMg&rart=ANgoxccdQg7crvfumAvpm2Ef_xsS_xadl47lGCZLe3j2pxB7WIr4QpDmm0fZXQc2_wiunlZ1nEfBMiilas7jsrfXbUBoBeH1zIyJGSq6h2bNCwHrCZxQdxM&rpbg=1&service=accountsettings' className='bold' target="_blank" rel="noreferrer">App Password</a></li>
                    </ol>
                    <span className='select-other'>Select 'Other' for both App and Device</span>
                    <Button rounded width="150px" onClick={() => setSelectedGoogleProviderStep(4)}>Next</Button>
                  </div>
                )}
                {selectedGoogleProviderStep === 4 && (
                  <Form form={form} onSubmit={handleSubmit}>
                    <div className="input-flex">
                      <Form.Item
                        label="First Name"
                        type="text"
                        colorBg
                        inputSm
                        name="first_name"
                        placeholder="Enter your first name"
                        rules={[
                          {
                            required: true,
                            message: 'First name is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                      <Form.Item
                        label="Last Name"
                        type="text"
                        colorBg
                        inputSm
                        name="last_name"
                        placeholder="Enter your last name"
                        rules={[
                          {
                            required: true,
                            message: 'Last name is required',
                          },
                        ]}>
                        <Field />
                      </Form.Item>
                    </div>
                    <Form.Item
                      label="Email Address"
                      type="text"
                      colorBg
                      inputSm
                      name="email"
                      placeholder="Enter your email address"
                      rules={[
                        {
                          required: true,
                          message: 'Email is required',
                        },
                      ]}>
                      <Field />
                    </Form.Item>
                    <span className='app-password-label'>App Password</span>
                    <span className='app-password-note'>Enter your 16 character app password</span>
                    <Form.Item
                      type="password"
                      colorBg
                      inputSm
                      name="app_password"
                      placeholder="Enter your app password"
                      rules={[
                        {
                          required: true,
                          message: 'App password is required',
                        },
                      ]}>
                      <Field />
                    </Form.Item>

                    <Button rounded type="submit" loader={loading} disabled={loading}>Connect</Button>
                  </Form>
                )}
              </div>
            </div>
          )}

          {activeTab === 'outlook' && (
            <div className="outlook-provider-holder">
              <div className="select-another-provider-holder" onClick={() => { setActiveTab('noSelection'); setSelectedOutlookProviderStep(1) }}><IoIosArrowBack /><span>Back</span> </div>
              <div className="options-wrapper">
                <div className="google-provider-head">
                  <img src={outlookIcon} alt="outlook" />
                  <div className='header-content'>
                    <h3>Connect Your Microsoft Account</h3>
                    <span>Office 365 / Outlook</span>
                  </div>
                </div>
                {selectedOutlookProviderStep === 1 && (
                  <>
                    <span className='heading'>First, let's <span className='bold'> Enable SMTP </span>access for your Microsoft account.</span>
                    <div className="col-holder">
                      <div className="col microsoft-col" onClick={() => setSelectedOutlookProviderStep(2)}>
                        <span>Microsoft accounts <span className='bold'>purchased directly from Microsoft</span></span>
                        <IoArrowForwardOutline size={24} color="var(--blue)" className='icon' />
                      </div>
                      {/* <div className="col" onClick={() => setSelectedOutlookProviderStep(3)}>
                        <span>Microsoft accounts <span className='bold'>purchased directly from GoDaddy</span></span>
                        <IoArrowForwardOutline size={24} color="var(--blue)" className='icon' />
                      </div> */}
                    </div>
                  </>
                )}
                {selectedOutlookProviderStep === 2 && (
                  <div className="oauth-holder">
                    <div className="head">
                      <div>
                        <h4>Microsoft accounts purchased directly from Microsoft</h4>
                        <span>Follow the instructions to enable SMTP first : </span>
                      </div>
                    </div>
                    <ol>
                      <li>On your computer, log in to your <a href="https://login.microsoftonline.com/common/oauth2/authorize?client_id=00000006-0000-0ff1-ce00-000000000000&response_type=code%20id_token&scope=openid%20profile&state=OpenIdConnect.AuthenticationProperties%3DmrHmrMFcgYeNZeQ2_b0qHtywQI9s6Fk1WYFUWGaz2o-GCAdMzdB_CbYKI3CnLcGOJLUmgTxmTyM7mRLvZEcE5affagJ6cOjVrKuWt7qaQBwzY3T6oTpUtciB_tmJVtzkLzJ5LW4uaENwOu9iAgEFczgoxEB3nAmQ4WFuvn7MaNgG3FYRyhnF-0S6-pLphgvXsaMcXb6jp89baMng__Dc76TEkgdLZX5Ch7X_j9r_dX8&response_mode=form_post&nonce=639087343690862899.M2IzN2NlYzgtMzMyMC00MjIxLWIwNDctNDFkMDZjYmM2ZmExYTk1OWU2ODYtZGEwNC00ZTZhLWFiZDMtZWY2YzUyN2M0MGEx&redirect_uri=https%3A%2F%2Fadmin.microsoft.com%2Flanding&ui_locales=en-US&mkt=en-US&client-request-id=7ec27827-7faf-49a1-8402-1e6c8a8178bc&claims=%7B%22id_token%22%3A%7B%22xms_cc%22%3A%7B%22values%22%3A%5B%22CP1%22%5D%7D%7D%7D&x-client-SKU=ID_NET472&x-client-ver=8.14.0.0&sso_reload=true" target="_blank" rel="noreferrer" className='bold'>Microsoft Admin center.</a></li>
                      <li>Open <a href='https://login.microsoftonline.com/common/oauth2/authorize?client_id=00000006-0000-0ff1-ce00-000000000000&response_type=code%20id_token&scope=openid%20profile&state=OpenIdConnect.AuthenticationProperties%3D1VwnyMpSvPN6uzPChkvTefE8Kjb51RU1Rn4SORaVe6PZLPAn4JYkTsfBXwLaHJi6NsJ07Jx3SKDvy1tWhtapiSKMDaQorX9viRvThWviLs6J6VAQVlze-5qqRQK_8o1yYrBPHZ8ZuW3C1ADXPKLijKuwh7Y5UKSf09LeB9K6nVFjisV1CM1pkZuhu5X1JP4xdQNqZ7TpC86O9Qftqco3CQ&response_mode=form_post&nonce=639087345250522483.NWMyMTQxYzgtYTA1ZC00ZTQzLThmMDEtNDI0ZmZmMjAzMGIxYjIxMzk0NDEtNGFhYi00ZjQ1LWI2ODEtZmYxNDUxYjg3MWE4&redirect_uri=https%3A%2F%2Fadmin.microsoft.com%2Flanding&ui_locales=en-US&mkt=en-US&client-request-id=7ec27827-7faf-49a1-8402-1e6c8a8178bc&claims=%7B%22id_token%22%3A%7B%22xms_cc%22%3A%7B%22values%22%3A%5B%22CP1%22%5D%7D%7D%7D&x-client-SKU=ID_NET472&x-client-ver=8.14.0.0' className='bold' target="_blank" rel="noreferrer">Active Users.</a></li>
                      <li>In the side window, click on Mail tab, and then on Manage email apps.</li>
                      <li>Check the Authenticated SMTP box and make sure IMAP is checked too.</li>
                      <li>Click Save Changes.</li>
                      <li>Wait for one hour and connect your account to Houdiny AI</li>
                    </ol>
                    <Button rounded width="300px" onClick={handleConnectWithOutLookAuth} loader={outlookAuthLoading} disabled={outlookAuthLoading}>Yes, SMTP has been enabled</Button>
                  </div>
                )}
                {selectedOutlookProviderStep === 3 && (
                  <div className="oauth-holder">
                    <div className="head">
                      <div>
                        <h4>GoDaddy accounts purchased directly from GoDaddy</h4>
                        <span>Follow the instructions to enable SMTP first : </span>
                      </div>
                      <div className="tutorial-holder">
                        <IoMdVideocam size={20} />
                        <span>Watch Tutorial Video</span>
                      </div>
                    </div>
                    <ol>
                      <li>On your computer, log in to your <span className='bold'>GoDaddy</span> account</li>
                      <li>Go to My Products page.</li>
                      <li>Scroll down and go to Email and Office section.</li>
                      <li>Click Manage All.</li>
                      <li>Find the user you want to enable SMTP for and click Manage.</li>
                      <li>Scroll down, click on Advanced Settings.</li>
                      <li>Click on SMTP Authentication - the button will turn from gray to green.</li>
                      <li>Wait for one hour and proceed to connect the account to Houdiny AI.</li>
                    </ol>
                    <Button rounded width="300px" onClick={handleConnectWithOutLookAuth} loader={outlookAuthLoading} disabled={outlookAuthLoading}>Yes, SMTP has been enabled</Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div >
    </EmailModalStyled >
  );
};

export default ConnectEmailModal;
